import { Stethoscope, ShieldAlert, ListChecks, AlertTriangle, BookOpen, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * PkiNbaGutachten · rendert das fachliche NBA-Gutachten (Fließtext, JSON-strukturiert)
 * als pflegefachlich gegliedertes Gutachten mit klaren Überschriften.
 *
 * Props:
 *   gutachten · {
 *     zusammenfassung, modul_1_mobilitaet ... modul_6_alltagsleben,
 *     pflegegrad_tendenz, begruendungsstaerke, fehlende_informationen[],
 *     md_empfehlungen[], risiken[], hinweis, _blocked_empfehlungen[]?
 *   }
 *   generatedAt · ISO-Date
 *   customerLabel · String
 */
export default function PkiNbaGutachten({ gutachten, generatedAt, customerLabel }) {
  if (!gutachten) return null;

  const bs = gutachten.begruendungsstaerke || "eingeschränkt";
  const eingeschraenkt = bs === "eingeschränkt" || bs === "eingeschraenkt";
  const schwach = bs === "schwach";

  const copyAsText = async () => {
    const text = buildGutachtenAsText(gutachten, customerLabel, generatedAt);
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Gutachten in die Zwischenablage kopiert.");
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  const moduleSections = [
    { key: "modul_1_mobilitaet",            label: "Modul 1 · Mobilität" },
    { key: "modul_2_kognition",             label: "Modul 2 · Kognition und Kommunikation" },
    { key: "modul_3_verhalten",             label: "Modul 3 · Verhaltensweisen und psychische Problemlagen" },
    { key: "modul_4_selbstversorgung",      label: "Modul 4 · Selbstversorgung" },
    { key: "modul_5_therapieanforderungen", label: "Modul 5 · Krankheits- und therapiebedingte Anforderungen" },
    { key: "modul_6_alltagsleben",          label: "Modul 6 · Alltagsleben und soziale Kontakte" },
  ];

  return (
    <article
      data-testid="pki-nba-gutachten"
      className="rounded-2xl border border-cream-300 bg-white p-6 md:p-8 space-y-6"
    >
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 pb-5 border-b border-cream-200">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-terra/12 text-terra h-11 w-11 flex items-center justify-center">
            <Stethoscope className="h-5 w-5" strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.28em] text-clay-dark font-bold">
              Fachliches NBA-Gutachten
            </div>
            <h2 className="font-display text-[1.6rem] text-sand-800 leading-tight mt-1">
              Pflegefachliche Einschätzung &amp; Begründung
            </h2>
            <div className="mt-1 text-[12px] text-sand-600">
              {customerLabel && <span>{customerLabel} · </span>}
              {generatedAt && new Date(generatedAt).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}
              {" · "}
              <BegruendungsstaerkePill value={bs} />
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={copyAsText}
          data-testid="pki-nba-gutachten-copy"
          className="h-9 rounded-full text-[12px] gap-1.5"
        >
          <Copy className="h-3.5 w-3.5" />
          Gutachten kopieren
        </Button>
      </header>

      {/* 1 · Zusammenfassende Einschätzung */}
      <Section
        title="1 · Zusammenfassende Einschätzung"
        testId="gutachten-zusammenfassung"
        body={gutachten.zusammenfassung}
      />

      {/* 2 · Modulbewertungen */}
      <div className="space-y-4" data-testid="gutachten-module">
        <SectionTitle small>2 · Bewertung der NBA-Module</SectionTitle>
        {moduleSections.map((m) => (
          <div
            key={m.key}
            data-testid={`gutachten-${m.key}`}
            className="rounded-xl border border-cream-200 bg-cream-50/40 p-4"
          >
            <div className="text-[11.5px] uppercase tracking-[0.22em] font-bold text-clay-dark mb-1.5">
              {m.label}
            </div>
            <p className="text-[14px] text-sand-800 leading-relaxed whitespace-pre-line">
              {gutachten[m.key] || "—"}
            </p>
          </div>
        ))}
      </div>

      {/* 3 · Pflegegrad-Tendenz */}
      <Section
        title="3 · Pflegegrad-Tendenz"
        testId="gutachten-pg-tendenz"
        body={gutachten.pflegegrad_tendenz}
        tone={eingeschraenkt ? "amber" : schwach ? "amber-soft" : "default"}
      />

      {/* 4 · Fehlende Informationen */}
      <ListSection
        title="4 · Fehlende Informationen"
        testId="gutachten-fehlende-informationen"
        items={gutachten.fehlende_informationen || []}
        icon={ListChecks}
        emptyLabel="Keine wesentlichen Datenlücken erkannt."
      />

      {/* 5 · MD-Empfehlungen */}
      <ListSection
        title="5 · Empfehlungen für die MD-Begutachtung"
        testId="gutachten-md-empfehlungen"
        items={gutachten.md_empfehlungen || []}
        icon={BookOpen}
        emptyLabel="Keine spezifischen Empfehlungen für die MD-Begutachtung."
      />

      {/* 5b · Geblockte Empfehlungen (falls Halluz-Schutz Höherstufung/Widerspruch entfernt hat) */}
      {(gutachten._blocked_empfehlungen || []).length > 0 && (
        <div
          data-testid="gutachten-blocked-empfehlungen"
          className="rounded-xl border border-brand-darkred/40 bg-brand-darkred/5 p-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="h-4 w-4 text-brand-darkred" strokeWidth={2} />
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-brand-darkred">
              Vom Halluzinationsschutz blockierte Vorschläge
            </div>
          </div>
          <p className="text-[12.5px] text-sand-700 mb-2 leading-snug">
            Die KI hat folgende Empfehlungen vorgeschlagen, die aufgrund unzureichender
            Datenlage entfernt wurden:
          </p>
          <ul className="space-y-1 text-[12.5px] text-sand-700">
            {(gutachten._blocked_empfehlungen || []).map((b, i) => (
              <li key={i} className="flex gap-2"><span className="text-brand-darkred">·</span><span className="line-through">{b}</span></li>
            ))}
          </ul>
        </div>
      )}

      {/* 6 · Risiken */}
      <ListSection
        title="6 · Pflegefachliche Risiken"
        testId="gutachten-risiken"
        items={gutachten.risiken || []}
        icon={AlertTriangle}
        emptyLabel="Aus den vorliegenden Daten ergeben sich keine spezifischen Risiken."
      />

      {/* Hinweis */}
      <div
        data-testid="gutachten-hinweis"
        className="rounded-xl border border-saffron/40 bg-saffron/12 p-4"
      >
        <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-clay-dark mb-1.5">
          Hinweis
        </div>
        <p className="text-[12.5px] text-sand-700 leading-relaxed italic">
          {gutachten.hinweis}
        </p>
      </div>
    </article>
  );
}

function Section({ title, body, testId, tone }) {
  const toneCls =
    tone === "amber"
      ? "border-brand-darkred/40 bg-brand-darkred/5"
      : tone === "amber-soft"
      ? "border-saffron/40 bg-saffron/8"
      : "border-cream-200 bg-cream-50/40";
  return (
    <section
      data-testid={testId}
      className={`rounded-xl border p-4 ${toneCls}`}
    >
      <div className="text-[11.5px] uppercase tracking-[0.22em] font-bold text-clay-dark mb-1.5">
        {title}
      </div>
      <p className="text-[14px] text-sand-800 leading-relaxed whitespace-pre-line">
        {body || "—"}
      </p>
    </section>
  );
}

function ListSection({ title, items, testId, icon: Icon, emptyLabel }) {
  return (
    <section
      data-testid={testId}
      className="rounded-xl border border-cream-200 bg-cream-50/40 p-4"
    >
      <div className="flex items-center gap-2 mb-1.5">
        {Icon && <Icon className="h-4 w-4 text-clay-dark" strokeWidth={1.8} />}
        <div className="text-[11.5px] uppercase tracking-[0.22em] font-bold text-clay-dark">
          {title}
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-[12.5px] text-sand-600 italic leading-snug">{emptyLabel}</p>
      ) : (
        <ul className="space-y-1.5 text-[13.5px] text-sand-800 leading-relaxed">
          {items.map((it, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-terra mt-0.5">·</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function BegruendungsstaerkePill({ value }) {
  const map = {
    "stark":          { cls: "bg-sage-dark/15 text-sage-dark border-sage-dark/40",   label: "Begründungsstärke · stark" },
    "mittel":         { cls: "bg-saffron/20 text-clay-dark border-saffron/60",       label: "Begründungsstärke · mittel" },
    "schwach":        { cls: "bg-peach-100 text-brand-darkred border-brand-darkred/35", label: "Begründungsstärke · schwach" },
    "eingeschränkt":  { cls: "bg-brand-darkred/15 text-brand-darkred border-brand-darkred/40", label: "Begründungsstärke · eingeschränkt" },
    "eingeschraenkt": { cls: "bg-brand-darkred/15 text-brand-darkred border-brand-darkred/40", label: "Begründungsstärke · eingeschränkt" },
  };
  const entry = map[value] || map["eingeschränkt"];
  return (
    <span
      data-testid="gutachten-begruendungsstaerke"
      className={`inline-flex items-center px-2 h-5 rounded-full border text-[10.5px] uppercase tracking-[0.18em] font-bold ${entry.cls}`}
    >
      {entry.label}
    </span>
  );
}

function SectionTitle({ children, small }) {
  return (
    <div className={`uppercase tracking-[0.22em] font-bold text-clay-dark ${small ? "text-[11.5px]" : "text-[13px]"}`}>
      {children}
    </div>
  );
}

/** Plain-Text-Export für Copy-Funktion (Pflegedoku-kompatibel) */
function buildGutachtenAsText(g, customerLabel, generatedAt) {
  const lines = [];
  lines.push("FACHLICHES NBA-GUTACHTEN");
  if (customerLabel) lines.push(`Kunde: ${customerLabel}`);
  if (generatedAt) lines.push(`Erstellt am: ${new Date(generatedAt).toLocaleString("de-DE")}`);
  lines.push(`Begründungsstärke: ${g.begruendungsstaerke || "—"}`);
  lines.push("");
  lines.push("1) ZUSAMMENFASSENDE EINSCHÄTZUNG");
  lines.push(g.zusammenfassung || "—");
  lines.push("");
  lines.push("2) BEWERTUNG DER NBA-MODULE");
  [
    ["Modul 1 · Mobilität",                                 g.modul_1_mobilitaet],
    ["Modul 2 · Kognition und Kommunikation",               g.modul_2_kognition],
    ["Modul 3 · Verhaltensweisen und psychische Problemlagen", g.modul_3_verhalten],
    ["Modul 4 · Selbstversorgung",                          g.modul_4_selbstversorgung],
    ["Modul 5 · Krankheits- und therapiebedingte Anforderungen", g.modul_5_therapieanforderungen],
    ["Modul 6 · Alltagsleben und soziale Kontakte",         g.modul_6_alltagsleben],
  ].forEach(([t, b]) => { lines.push(t); lines.push(b || "—"); lines.push(""); });
  lines.push("3) PFLEGEGRAD-TENDENZ");
  lines.push(g.pflegegrad_tendenz || "—");
  lines.push("");
  lines.push("4) FEHLENDE INFORMATIONEN");
  (g.fehlende_informationen || []).forEach((x) => lines.push("- " + x));
  lines.push("");
  lines.push("5) EMPFEHLUNGEN FÜR DIE MD-BEGUTACHTUNG");
  (g.md_empfehlungen || []).forEach((x) => lines.push("- " + x));
  lines.push("");
  lines.push("6) PFLEGEFACHLICHE RISIKEN");
  (g.risiken || []).forEach((x) => lines.push("- " + x));
  lines.push("");
  lines.push("HINWEIS");
  lines.push(g.hinweis || "");
  return lines.join("\n");
}
